import React from 'react';
import { useTranslation } from 'react-i18next';
import { Result, Button, Card, Typography, Space, Row, Col } from 'antd';
import {
  LockOutlined,
  HomeOutlined,
  SecurityScanOutlined,
  UserOutlined,
  SafetyCertificateOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';

const { Title, Paragraph, Text } = Typography;

interface UnauthorizedAccessProps {
  userRole?: string;
  userName?: string;
  user?: {
    firstName?: string;
    lastName?: string;
    email?: string;
    role?: string;
  };
}

export const UnauthorizedAccess: React.FC<UnauthorizedAccessProps> = ({
  userRole,
  userName,
  user,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleGoHome = () => {
    navigate('/');
  };

  const handleContactAdmin = () => {
    // You can implement contact admin functionality here
    window.open(
      'mailto:admin@AutoPlace.com?subject=Admin Access Request',
      '_blank'
    );
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
      }}
    >
      <Card
        style={{
          maxWidth: 800,
          width: '100%',
          borderRadius: 16,
          boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
        }}
      >
        <Result
          icon={
            <div
              style={{
                fontSize: 120,
                color: '#ff4d4f',
                marginBottom: 24,
              }}
            >
              <SafetyCertificateOutlined />
            </div>
          }
          title={
            <Title level={2} style={{ color: '#262626', marginBottom: 16 }}>
              {t('admin.access_restricted')}
            </Title>
          }
          subTitle={
            <div style={{ marginBottom: 32 }}>
              <Paragraph
                style={{ fontSize: 16, color: '#595959', marginBottom: 16 }}
              >
                {t('admin.no_permissions')}
              </Paragraph>

              <Card
                size="small"
                style={{
                  background: '#f6ffed',
                  border: '1px solid #b7eb8f',
                  marginBottom: 24,
                }}
              >
                <Space direction="vertical" style={{ width: '100%' }}>
                  <div
                    style={{ display: 'flex', alignItems: 'center', gap: 8 }}
                  >
                    <UserOutlined style={{ color: '#52c41a' }} />
                    <Text strong style={{ color: '#52c41a' }}>
                      {t('admin.current_user')}:{' '}
                      {user
                        ? `${user.firstName} ${user.lastName}`
                        : userName || 'Unknown'}
                    </Text>
                  </div>
                  <div
                    style={{ display: 'flex', alignItems: 'center', gap: 8 }}
                  >
                    <SecurityScanOutlined style={{ color: '#faad14' }} />
                    <Text style={{ color: '#faad14' }}>
                      {t('admin.role_label')}: {user?.role || userRole || 'User'}
                    </Text>
                  </div>
                </Space>
              </Card>

              <div
                style={{
                  background: '#fff7e6',
                  padding: 16,
                  borderRadius: 8,
                  border: '1px solid #ffd591',
                }}
              >
                <Title level={5} style={{ color: '#d46b08', marginBottom: 12 }}>
                  <LockOutlined style={{ marginRight: 8 }} />
                  {t('admin.why_restricted')}
                </Title>
                <ul style={{ color: '#d46b08', marginBottom: 0 }}>
                  <li>{t('admin.requires_role')}</li>
                  <li>{t('admin.no_permissions_desc')}</li>
                  <li>{t('admin.contact_admin')}</li>
                </ul>
              </div>
            </div>
          }
          extra={
            <Row gutter={[16, 16]} justify="center">
              <Col>
                <Button
                  type="primary"
                  size="large"
                  icon={<HomeOutlined />}
                  onClick={handleGoHome}
                  style={{
                    height: 48,
                    paddingLeft: 24,
                    paddingRight: 24,
                    borderRadius: 8,
                  }}
                >
                  {t('admin.go_home')}
                </Button>
              </Col>
              <Col>
                <Button
                  size="large"
                  icon={<SecurityScanOutlined />}
                  onClick={handleContactAdmin}
                  style={{
                    height: 48,
                    paddingLeft: 24,
                    paddingRight: 24,
                    borderRadius: 8,
                  }}
                >
                  {t('admin.request_access')}
                </Button>
              </Col>
            </Row>
          }
        />

        <div
          style={{
            marginTop: 32,
            padding: 20,
            background: '#fafafa',
            borderRadius: 8,
            border: '1px solid #d9d9d9',
          }}
        >
          <Title level={5} style={{ color: '#262626', marginBottom: 16 }}>
            {t('admin.need_help')}
          </Title>
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12}>
              <div
                style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}
              >
                <SecurityScanOutlined
                  style={{ color: '#1890ff', fontSize: 20, marginTop: 4 }}
                />
                <div>
                  <Text strong style={{ color: '#262626' }}>
                    {t('admin.contact_admin_title')}
                  </Text>
                  <br />
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {t('admin.contact_admin_desc')}
                  </Text>
                </div>
              </div>
            </Col>
            <Col xs={24} sm={12}>
              <div
                style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}
              >
                <SafetyCertificateOutlined
                  style={{ color: '#52c41a', fontSize: 20, marginTop: 4 }}
                />
                <div>
                  <Text strong style={{ color: '#262626' }}>
                    {t('admin.security_policy')}
                  </Text>
                  <br />
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {t('admin.security_policy_desc')}
                  </Text>
                </div>
              </div>
            </Col>
          </Row>
        </div>
      </Card>
    </div>
  );
};

export default UnauthorizedAccess;
